import { useState, useEffect, useCallback } from "react";
import { sendAvailabilityCheck, sendIndividualCheck, overrideCrewStatus, getAvailabilityChecks } from "@/lib/api";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { MessageSquare, Check, X, Clock, RefreshCw, Send, Bell, Users, CalendarDays } from "lucide-react";

const STATUS_CONFIG = {
  yes:      { icon: Check, color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/20", label: "Confirmed" },
  no:       { icon: X,     color: "text-red-400",     bg: "bg-red-500/10",     border: "border-red-500/20",     label: "Declined" },
  pending:  { icon: Clock, color: "text-amber-400",   bg: "bg-amber-500/10",   border: "border-amber-500/20",   label: "Pending" },
  unsent:   { icon: Send,  color: "text-muted-foreground/50", bg: "bg-secondary/20", border: "border-border", label: "Not contacted" },
};

export function CrewStatusSection({ event, crew, canEdit }) {
  const [checks, setChecks] = useState([]);
  const [sending, setSending] = useState({});
  const [overriding, setOverriding] = useState({});
  const [bulkSending, setBulkSending] = useState(false);
  const [crewDates, setCrewDates] = useState(event.crew_dates || {});

  const crewIds = event.crew_assignments || [];
  const isMultiDay = event.start_date && event.end_date && event.start_date !== event.end_date;

  // Generate all dates in the event range
  const eventDateRange = (() => {
    if (!isMultiDay) return [];
    const dates = [];
    const start = new Date(event.start_date + "T00:00:00");
    const end = new Date(event.end_date + "T00:00:00");
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      dates.push(d.toISOString().split("T")[0]);
    }
    return dates;
  })();

  const fetchChecks = useCallback(async () => {
    try {
      const res = await getAvailabilityChecks(event.event_id);
      setChecks(res.data);
    } catch (err) { toast.error("Failed to load availability checks"); }
  }, [event.event_id]);

  useEffect(() => { fetchChecks(); }, [fetchChecks]);

  const checksByCrewId = {};
  checks.forEach(c => { checksByCrewId[c.crew_id] = c; });

  const getCrewName = (id) => crew.find(c => c.crew_id === id)?.name || id;

  const handleSendIndividual = async (crewId, isReminder = false) => {
    setSending(prev => ({ ...prev, [crewId]: true }));
    try {
      await sendIndividualCheck(event.event_id, crewId, { reminder: isReminder });
      toast.success(isReminder ? "Reminder sent" : "Availability check sent");
      fetchChecks();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send SMS");
    } finally {
      setSending(prev => ({ ...prev, [crewId]: false }));
    }
  };

  const handleOverride = async (crewId, newStatus) => {
    setOverriding(prev => ({ ...prev, [crewId]: true }));
    try {
      const res = await overrideCrewStatus(event.event_id, crewId, newStatus);
      const smsNote = res.data.sms_sent ? " (SMS sent)" : " (no SMS — check phone)";
      toast.success(`${getCrewName(crewId)} marked as ${newStatus}${smsNote}`);
      fetchChecks();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update status");
    } finally {
      setOverriding(prev => ({ ...prev, [crewId]: false }));
    }
  };

  const handleBulkSend = async (force = false) => {
    setBulkSending(true);
    try {
      const res = await sendAvailabilityCheck(event.event_id, { force });
      const { total_sent, total_skipped, skipped } = res.data;
      if (total_sent > 0) toast.success(`Sent ${total_sent} check${total_sent > 1 ? 's' : ''}`);
      if (total_skipped > 0) {
        const reasons = skipped.map(s => `${s.name}: ${s.reason}`).join(", ");
        toast.info(`Skipped ${total_skipped}: ${reasons}`);
      }
      fetchChecks();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send checks");
    } finally {
      setBulkSending(false);
    }
  };

  const toggleCrewDate = async (crewId, dateStr) => {
    const current = crewDates[crewId] || [...eventDateRange]; // default: all dates
    const updated = current.includes(dateStr)
      ? current.filter(d => d !== dateStr)
      : [...current, dateStr].sort();

    setCrewDates(prev => ({ ...prev, [crewId]: updated }));
    try {
      await api.put(`/events/${event.event_id}/crew-dates/${crewId}`, { dates: updated });
    } catch (err) {
      toast.error("Failed to update crew dates");
      setCrewDates(prev => ({ ...prev, [crewId]: current }));
    }
  };

  if (crewIds.length === 0) return null;

  const yesCount = checks.filter(c => c.status === "yes").length;
  const noCount = checks.filter(c => c.status === "no").length;
  const pendingCount = checks.filter(c => c.status === "pending").length;
  const unsentCount = crewIds.length - checks.length;

  return (
    <div className="mt-2 pt-2 border-t border-border" data-testid={`crew-status-section-${event.event_id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Users className="w-3 h-3 text-muted-foreground" />
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Crew ({crewIds.length})</span>
          <div className="flex items-center gap-1 ml-1">
            {yesCount > 0 && <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-emerald-500/10 text-emerald-400">{yesCount}</Badge>}
            {noCount > 0 && <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-red-500/10 text-red-400">{noCount}</Badge>}
            {pendingCount > 0 && <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-amber-500/10 text-amber-400">{pendingCount}</Badge>}
            {unsentCount > 0 && <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{unsentCount} unsent</Badge>}
          </div>
        </div>
        {canEdit && (
          <div className="flex items-center gap-1">
            <Button data-testid={`avail-refresh-${event.event_id}`} variant="ghost" size="icon" className="h-6 w-6" onClick={fetchChecks}>
              <RefreshCw className="w-3 h-3" />
            </Button>
            <Button
              data-testid={`avail-send-all-${event.event_id}`}
              variant="ghost" size="sm" className="h-6 text-xs text-primary"
              onClick={() => handleBulkSend(false)} disabled={bulkSending}
            >
              <MessageSquare className="w-3 h-3 mr-1" />{bulkSending ? "Sending..." : "Send All"}
            </Button>
            {pendingCount > 0 && (
              <Button
                data-testid={`avail-remind-all-${event.event_id}`}
                variant="ghost" size="sm" className="h-6 text-xs text-amber-400"
                onClick={() => handleBulkSend(true)} disabled={bulkSending}
              >
                <Bell className="w-3 h-3 mr-1" />Remind All
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="space-y-1">
        {crewIds.map(crewId => {
          const check = checksByCrewId[crewId];
          const status = check?.status || "unsent";
          const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.unsent;
          const Icon = cfg.icon;
          const name = getCrewName(crewId);
          const isLoading = sending[crewId] || overriding[crewId];

          return (
            <div
              key={crewId}
              data-testid={`crew-row-${crewId}`}
              className={`px-2.5 py-2 rounded-sm border ${cfg.border} ${cfg.bg} transition-all`}
            >
              <div className="flex flex-wrap items-center justify-between gap-y-1.5">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <Icon className={`w-3.5 h-3.5 ${cfg.color} shrink-0`} />
                  <span className="text-sm font-medium truncate">{name}</span>
                  {check?.source === "manual" && <Badge variant="outline" className="text-[10px] px-1 py-0 h-3.5 opacity-60">manual</Badge>}
                  {check?.reminder_count > 0 && <Badge variant="outline" className="text-[10px] px-1 py-0 h-3.5 opacity-60">{check.reminder_count}x reminded</Badge>}
                  {check?.available_dates?.length > 0 && (
                    <Badge variant="outline" className="text-[10px] px-1 py-0 h-3.5 text-sky-400 border-sky-500/20">
                      replied: {check.available_dates.map(d => new Date(d + "T00:00:00").getDate()).join(", ")}
                    </Badge>
                  )}
                </div>

                <div className="flex items-center gap-1.5 shrink-0">
                  {canEdit && (
                    <>
                      <Select
                        value={status === "yes" ? "confirmed" : status === "no" ? "declined" : status === "pending" ? "pending" : ""}
                        onValueChange={(val) => handleOverride(crewId, val)}
                        disabled={isLoading}
                      >
                        <SelectTrigger
                          data-testid={`crew-status-select-${crewId}`}
                          className={`h-6 w-[100px] text-xs border-0 ${cfg.bg} ${cfg.color} focus:ring-0`}
                        >
                          <SelectValue placeholder={cfg.label} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="confirmed"><span className="flex items-center gap-1.5"><Check className="w-3 h-3 text-emerald-400" />Confirmed</span></SelectItem>
                          <SelectItem value="declined"><span className="flex items-center gap-1.5"><X className="w-3 h-3 text-red-400" />Declined</span></SelectItem>
                          <SelectItem value="pending"><span className="flex items-center gap-1.5"><Clock className="w-3 h-3 text-amber-400" />Pending</span></SelectItem>
                        </SelectContent>
                      </Select>

                      {status === "unsent" && (
                        <Button data-testid={`crew-send-${crewId}`} variant="ghost" size="icon" className="h-6 w-6 text-primary"
                          onClick={() => handleSendIndividual(crewId)} disabled={isLoading} title="Send availability check">
                          <Send className="w-3 h-3" />
                        </Button>
                      )}
                      {status === "pending" && (
                        <Button data-testid={`crew-remind-${crewId}`} variant="ghost" size="icon" className="h-6 w-6 text-amber-400"
                          onClick={() => handleSendIndividual(crewId, true)} disabled={isLoading} title="Send reminder">
                          <Bell className="w-3 h-3" />
                        </Button>
                      )}
                    </>
                  )}
                  {!canEdit && <Badge variant="secondary" className={`text-xs px-1.5 py-0 ${cfg.bg} ${cfg.color}`}>{cfg.label}</Badge>}
                </div>
              </div>

              {/* Date chips for multi-day events */}
              {isMultiDay && canEdit && (
                <div data-testid={`crew-dates-${crewId}`} className="flex flex-wrap items-center gap-1 mt-1.5 pl-5.5">
                  <CalendarDays className="w-3 h-3 text-muted-foreground shrink-0" />
                  {eventDateRange.map(dateStr => {
                    const assignedDates = crewDates[crewId] || eventDateRange;
                    const isActive = assignedDates.includes(dateStr);
                    const dayNum = new Date(dateStr + "T00:00:00").getDate();
                    const dayName = new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", { weekday: "short" });
                    return (
                      <button
                        key={dateStr}
                        data-testid={`crew-date-chip-${crewId}-${dateStr}`}
                        onClick={() => toggleCrewDate(crewId, dateStr)}
                        className={`text-[10px] font-medium px-1.5 py-0.5 rounded-sm border transition-all cursor-pointer ${
                          isActive
                            ? "bg-primary/15 text-primary border-primary/30 hover:bg-primary/25"
                            : "bg-secondary/20 text-muted-foreground/50 border-border/50 line-through hover:bg-secondary/40"
                        }`}
                        title={`${isActive ? "Remove" : "Add"} ${dayName} ${dayNum}`}
                      >
                        {dayName} {dayNum}
                      </button>
                    );
                  })}
                </div>
              )}
              {isMultiDay && !canEdit && crewDates[crewId] && crewDates[crewId].length < eventDateRange.length && (
                <div className="flex items-center gap-1 mt-1 pl-5.5">
                  <CalendarDays className="w-3 h-3 text-muted-foreground shrink-0" />
                  <span className="text-[10px] text-muted-foreground">
                    Working: {crewDates[crewId].map(d => new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })).join(", ")}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
